import type { AccommodationQuantityType } from "@@/shared/acquire/accommodation-quantity-types";
import { accommodationQuantityTypeZodEnum } from "@@/shared/acquire/accommodation-quantity-types";
import { type Branded } from "@@/utilities/branded";
import { genericId } from "@@/utilities/generic-id";
import {
    minMaxRangeZodSchema,
    type MinMaxRange,
} from "@@/utilities/set-within-range";
import type { Except } from "type-fest";
import { z } from "zod";

import type { ExtrasCondition } from "@@/shared/acquire/extras-conditions";
import { extrasConditionZodSchema } from "@@/shared/acquire/extras-conditions";
import type { BookableOptionId } from "@@/shared/acquire/resource-reference-options/bookable-option-id";
import { bookableOptionIdZodSchema } from "@@/shared/acquire/resource-reference-options/bookable-option-id";
import type { MetaContent } from "@@/shared/meta-content/meta-content";
import { metaContentZodSchema } from "@@/shared/meta-content/meta-content";
import { productIdZodSchema, type ProductId } from "../products/product-id";

type ExtraGroupId = Branded<string, "ExtraGroupId">;
const idPrefix = "extras_group__";
const {
    factory: extrasIdFactory,
    assert: isExtrasGroupId,
    zodSchema: extraGroupIdZodSchema,
} = genericId<ExtraGroupId>(idPrefix, {
    regexMatch: /(^extras_group_.*$)/,
});

const extraProductConditionZodSchema = z.object({
    _id: z.string(),
    _type: z.literal("EXTRAS_PRODUCT_CONDITION"),
});

const extraProductZodSchema = z.object({
    productId: productIdZodSchema,
    conditions: z.array(extraProductConditionZodSchema),
});

const minAmountLimitZodSchema = z.union([
    z.literal("ZERO"),
    z.literal("LIMITED_TO_BOOKABLE_AMOUNT"),
]);

const maxAmountLimitZodSchema = z.union([
    z.literal("UNLIMITED"),
    z.literal("LIMITED_TO_BOOKABLE_AMOUNT"),
]);

const extrasBookableOptionAmountSettingZodSchema = z.object({
    _type: z.literal("BOOKABLE_OPTION_AMOUNT"),
    bookableOptions: z.array(
        z.union([bookableOptionIdZodSchema, accommodationQuantityTypeZodEnum]),
    ),
    min: minAmountLimitZodSchema,
    max: maxAmountLimitZodSchema,
});

const extrasFreeAmountSettingZodSchema = z.object({
    _type: z.literal("FREE_AMOUNT"),
});

const extrasMinMaxSettingZodSchema = z.object({
    _type: z.literal("MIN_MAX_AMOUNT"),
    minMax: minMaxRangeZodSchema,
});

const extrasAmountSettingZodSchema = z.union([
    extrasFreeAmountSettingZodSchema,
    extrasBookableOptionAmountSettingZodSchema,
    extrasMinMaxSettingZodSchema,
]);

const extrasGroupZodSchema: z.ZodSchema<ExtrasGroup> = z.object({
    _id: extraGroupIdZodSchema,
    _type: z.literal("EXTRAS_GROUP"),
    meta: metaContentZodSchema,
    products: z.array(extraProductZodSchema),
    productIds: z.array(productIdZodSchema).optional(),
    conditions: z.array(extrasConditionZodSchema),
    settings: extrasAmountSettingZodSchema,
});

//Wip
type ExtraProductCondition = {
    _id: string;
    _type: "EXTRAS_PRODUCT_CONDITION";
};

type ExtrasGroup = {
    _id: ExtraGroupId;
    _type: "EXTRAS_GROUP";
    meta: MetaContent;
    //Valid for products
    products: ExtraProduct[];

    productIds?: ProductId[];

    conditions: ExtrasCondition[];
    settings: ExtrasAmountSetting;
};

type ExtraProduct = {
    productId: ProductId;
    conditions: ExtraProductCondition[];
};

// Separate type for Min and Max to avoid confusion and allow individual modification
type MinAmountLimit = "ZERO" | "LIMITED_TO_BOOKABLE_AMOUNT";
type MaxAmountLimit = "UNLIMITED" | "LIMITED_TO_BOOKABLE_AMOUNT";

// Shared type to use for both ExtrasBookableOptionAmountSetting and ExtrasBookableOptionPerAmountSetting
type ExtrasBookableOptionAmountSettingBase = {
    min: MinAmountLimit;
    max: MaxAmountLimit;
};

// Use ExtrasBookableOptionAmountSettingBase and extend it with unique properties
type ExtrasBookableOptionAmountSetting =
    ExtrasBookableOptionAmountSettingBase & {
        _type: "BOOKABLE_OPTION_AMOUNT";
        // Bookable or AccommodationQuantityType name change but how, or new that works the same but for accommodations
        bookableOptions: (BookableOptionId | AccommodationQuantityType)[];
    };

type ExtrasFreeAmountSetting = {
    _type: "FREE_AMOUNT";
};

type ExtrasMinMaxSetting = {
    _type: "MIN_MAX_AMOUNT";
    minMax: MinMaxRange;
};

type FactoryParams = Except<ExtrasGroup, "_id" | "_type">;

const extrasGroupsFactory = (params: FactoryParams): ExtrasGroup => {
    return {
        _id: extrasIdFactory(),
        _type: "EXTRAS_GROUP",
        meta: params.meta,
        conditions: params.conditions,
        products: params.products,
        settings: params.settings,
    };
};

const calculateMinMaxRange = (
    extras: ExtrasGroup,
    amount: number,
): MinMaxRange => {
    switch (extras.settings._type) {
        case "FREE_AMOUNT":
            return { min: undefined, max: undefined };
        case "MIN_MAX_AMOUNT":
            return extras.settings.minMax;
        case "BOOKABLE_OPTION_AMOUNT": {
            const max =
                extras.settings.max === "UNLIMITED" ? undefined : amount;
            const min = extras.settings.min === "ZERO" ? undefined : amount;
            return { min, max };
        }
    }
};

type ExtrasAmountSetting =
    | ExtrasFreeAmountSetting
    | ExtrasBookableOptionAmountSetting
    | ExtrasMinMaxSetting;

//Todo naming
const fromExtrasZodSchema = z.object({
    _id: extraGroupIdZodSchema,
    meta: metaContentZodSchema,
});

export {
    calculateMinMaxRange,
    extraGroupIdZodSchema,
    extrasGroupsFactory,
    extrasGroupZodSchema,
    extrasIdFactory,
    fromExtrasZodSchema,
    isExtrasGroupId,
};
export type {
    ExtraGroupId,
    ExtrasAmountSetting,
    ExtrasBookableOptionAmountSetting,
    ExtrasFreeAmountSetting,
    ExtrasGroup,
    ExtrasMinMaxSetting,
};
